// Unlock-schedule CRUD at `/schedules`. Visible to anyone with
// TENANT_MANAGE_SCHEDULES (master_admin, tenant_admin, tenant_manager).
//
// P1-8 (Agent C): replace the list-only V2 scaffold with real Create /
// Edit / Delete workflows. Every mutation hits the controller via the
// API, so every modal owns a single `Idempotency-Key` for the lifetime
// of that user-submitted intent (per apps/web/CLAUDE.md).
//
// Overlap validation: the backend rejects overlapping windows on the
// same weekday with a 400 + DomainError envelope. We surface that
// message verbatim under the windows section. The Zod schema in
// @ng-unifi/domain catches the same case during client-side build of
// the body.
//
// Sync status: each row shows `sync_status` and (when failed) the
// `last_sync_error` text on hover/expand.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError, type ApiRequestInit } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { useSiteMode } from '../auth/SiteModeContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTenantScopedPage } from '../lib/useTenantScopedPage.js';
import {
  commonHolidayPresets,
  mergeClosedHolidayOverrides,
  removeHolidayOverride,
  type HolidayOverride,
  type ScheduleWindow,
  type Weekday,
} from '../lib/holidayPresets.js';

const WEEKDAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// GAP-H3 (audit 2026-05-21): "access groups" is foreign vocabulary in
// multi_family — operators think in unit / amenity terms. Mode-aware
// subtitle keeps the explanation operator-shaped.
const SCHEDULES_SUBTITLE: Record<string, string> = {
  commercial_multi_tenant:
    'Time windows that govern when access groups can open doors.',
  single_tenant:
    'Time windows that govern when access groups can open doors.',
  multi_family:
    'Time windows for amenities, doors, and resident schedules.',
};

interface ScheduleRow {
  id: string;
  tenant_id?: string;
  name: string;
  windows_json: ScheduleWindow[];
  holiday_overrides_json: HolidayOverride[];
  unifi_schedule_id: string | null;
  sync_status: 'synced' | 'pending' | 'failed';
  last_sync_error: string | null;
  last_sync_at: string | null;
  created_at: string;
  updated_at: string;
}

const SYNC_TONE: Record<ScheduleRow['sync_status'], string> = {
  synced: 'bg-secondary-container text-on-secondary-container',
  pending: 'bg-tertiary-fixed text-on-tertiary-fixed',
  failed: 'bg-error-container text-on-error-container',
};

async function loadSchedules(scopeOpts?: ApiRequestInit): Promise<ScheduleRow[]> {
  const res = await apiClient.get<{ data: ScheduleRow[] }>(
    '/api/v1/schedules',
    scopeOpts,
  );
  return res.data;
}

async function loadBuildingSchedules(): Promise<ScheduleRow[]> {
  const res = await apiClient.get<{ data: ScheduleRow[] }>(
    '/api/v1/admin/schedules',
    { skipTenantScope: true },
  );
  return res.data;
}

type ModalState =
  | { kind: 'create' }
  | { kind: 'edit'; schedule: ScheduleRow }
  | { kind: 'delete'; schedule: ScheduleRow };

export function SchedulesPage() {
  const siteMode = useSiteMode();
  const auth = useAuth();
  const { tenantName, scopeOpts, effectiveTenantId, scopedDetailHref } = useTenantScopedPage();
  const isBuildingView =
    auth.state.status === 'authed' &&
    auth.state.user.role === 'master_admin' &&
    auth.state.user.tenant_id == null &&
    effectiveTenantId == null;
  const [rows, setRows] = useState<ScheduleRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  // UX-014: ScheduleDetailPage's "Edit" navigated here with ?focus=<id>, which
  // nothing read. Editing lives in this page's modal, so honour ?edit=<id> —
  // the same convention EmployeeDetailPage uses — and strip it once consumed
  // so a refresh does not reopen the dialog.
  useEffect(() => {
    if (!rows) return;
    const wanted = searchParams.get('edit');
    if (!wanted) return;
    const schedule = rows.find((r) => r.id === wanted);
    if (schedule) setModal({ kind: 'edit', schedule });
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [rows, searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    const loader = isBuildingView ? loadBuildingSchedules : () => loadSchedules(scopeOpts);
    loader()
      .then((s) => {
        if (!cancelled) setRows(s);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : 'Failed to load schedules',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isBuildingView, reloadTick, scopeOpts]);

  return (
    <div className="space-y-6">
      {/* flex-wrap + min-w-0: at 320px the icon, title and the action button
          cannot share one row, and the button is what has to move. Same
          header shape as HouseholdsPage. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="schedule" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Unlock schedules
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              {isBuildingView
                ? 'Building View schedules for shared/common-area door behavior.'
                : tenantName
                  ? `Viewing ${tenantName} schedules.`
                  : // siteMode is a typed literal union; SCHEDULES_SUBTITLE is a closed code-defined map.
                    // eslint-disable-next-line security/detect-object-injection
                    SCHEDULES_SUBTITLE[siteMode] ?? SCHEDULES_SUBTITLE.commercial_multi_tenant}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setModal({ kind: 'create' })}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary font-label-md text-label-md rounded-lg hover:bg-opacity-90 transition-all"
        >
          <Icon name="add" />
          <span>New schedule</span>
        </button>
      </header>

      {toast ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md flex items-center gap-2"
        >
          <Icon name="check_circle" />
          <span className="flex-1">{toast}</span>
          <button
            type="button"
            onClick={() => setToast(null)}
            className="underline font-medium"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <section className="bg-surface-container-lowest border border-outline-variant rounded-xl overflow-hidden">
        {error ? (
          <div
            className="p-6 text-error font-body-md text-body-md"
            role="alert"
          >
            {error}
          </div>
        ) : rows == null ? (
          <div className="p-12 flex justify-center">
            <Spinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-on-surface-variant font-body-md text-body-md">
            No schedules yet. Build one with the button above.
          </div>
        ) : (
          // A table's min-content width is set by its columns, not the
          // viewport, so at 320px it measured 593px and took the page with
          // it. Wide content scrolls inside its own container; the page
          // itself must never scroll sideways.
          <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem]">
            <thead className="bg-surface-container">
              <tr>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[28%]">
                  Name
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3">
                  Windows
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[14%]">
                  Controller
                </th>
                <th className="text-left font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[14%]">
                  Last sync
                </th>
                <th className="text-right font-label-md text-label-md text-on-surface-variant uppercase px-4 py-3 w-[16%]">
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {rows.map((s) => (
                <ScheduleRowView
                  key={s.id}
                  row={s}
                  buildingView={isBuildingView}
                  scopedDetailHref={scopedDetailHref}
                  expanded={expanded === s.id}
                  onToggle={() =>
                    setExpanded(expanded === s.id ? null : s.id)
                  }
                  onEdit={() => setModal({ kind: 'edit', schedule: s })}
                  onDelete={() => setModal({ kind: 'delete', schedule: s })}
                />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {modal?.kind === 'create' ? (
        <CreateOrEditModal
          schedule={null}
          buildingView={isBuildingView}
          scopeOpts={scopeOpts}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : modal?.kind === 'edit' ? (
        <CreateOrEditModal
          schedule={modal.schedule}
          buildingView={isBuildingView}
          scopeOpts={scopeOpts}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : modal?.kind === 'delete' ? (
        <DeleteModal
          schedule={modal.schedule}
          buildingView={isBuildingView}
          scopeOpts={scopeOpts}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ScheduleRowView({
  row,
  buildingView,
  scopedDetailHref,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: {
  row: ScheduleRow;
  buildingView: boolean;
  scopedDetailHref: (
    basePath: string,
    resourceId: string,
    extraQuery?: Record<string, string>,
    tenantOverride?: string | null,
  ) => string | null;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const windowSummary = useMemo(() => {
    const ws = row.windows_json ?? [];
    if (ws.length === 0) return '—';
    const first = ws[0];
    if (!first) return '—';
    const more = ws.length - 1;
    const label = formatWindow(first);
    return more > 0 ? `${label} (+${more} more)` : label;
  }, [row.windows_json]);

  return (
    <>
      <tr className="hover:bg-surface-container-low transition-colors">
        <td className="px-4 py-3 font-body-md text-body-md text-on-surface">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onToggle}
              className="inline-flex items-center gap-1 text-left hover:underline"
              aria-expanded={expanded}
            >
              <Icon
                name={expanded ? 'expand_more' : 'chevron_right'}
                className="text-base"
              />
            </button>
            {buildingView ? (
              <span>{row.name}</span>
            ) : (
              <Link
                to={scopedDetailHref('/schedules', row.id, undefined, row.tenant_id) ?? '#'}
                className="text-primary hover:underline"
                data-testid={`schedule-row-link-${row.id}`}
              >
                {row.name}
              </Link>
            )}
          </div>
        </td>
        <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
          {windowSummary}
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase ${SYNC_TONE[row.sync_status]}`}
            title={row.last_sync_error ?? undefined}
          >
            {row.sync_status}
          </span>
        </td>
        <td className="px-4 py-3 font-body-md text-body-md text-on-surface-variant">
          {row.last_sync_at
            ? new Date(row.last_sync_at).toLocaleString()
            : '—'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-container-high"
              title="Edit"
              aria-label={`Edit ${row.name}`}
            >
              <Icon name="edit" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-error-container hover:text-on-error-container"
              title="Delete"
              aria-label={`Delete ${row.name}`}
            >
              <Icon name="delete" />
            </button>
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr className="bg-surface-container-low">
          <td colSpan={5} className="px-6 py-4">
            <ScheduleDetail row={row} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ScheduleDetail({ row }: { row: ScheduleRow }) {
  return (
    <div className="space-y-3">
      {row.sync_status === 'failed' && row.last_sync_error ? (
        <div
          role="alert"
          className="bg-error-container text-on-error-container rounded-lg px-3 py-2 font-body-md text-body-md"
        >
          <Icon name="error" className="inline-block mr-1" />
          <span>Last sync error: {row.last_sync_error}</span>
        </div>
      ) : null}
      <div className="space-y-2">
        <h3 className="font-label-md text-label-md text-on-surface uppercase">
          Windows
        </h3>
        {(row.windows_json ?? []).length === 0 ? (
          <p className="text-on-surface-variant font-body-md text-body-md">
            None defined.
          </p>
        ) : (
          <ul className="space-y-1">
            {(row.windows_json ?? []).map((w, idx) => (
              <li
                key={`${idx}-${w.start}-${w.end}`}
                className="font-body-md text-body-md text-on-surface"
              >
                <span className="font-medium">{w.days.join(', ')}</span>{' '}
                <span className="text-on-surface-variant">
                  {isAllDayWindow(w) ? 'All day' : `${w.start}-${w.end}`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {(row.holiday_overrides_json ?? []).length > 0 ? (
        <div className="space-y-2">
          <h3 className="font-label-md text-label-md text-on-surface uppercase">
            Holiday overrides
          </h3>
          <ul className="space-y-1">
            {(row.holiday_overrides_json ?? []).map((h, idx) => (
              <li
                key={`${idx}-${h.date}`}
                className="font-body-md text-body-md text-on-surface"
              >
                <span className="font-medium">{h.date}</span>{' '}
                <span className="text-on-surface-variant">— {h.mode}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <p className="font-body-md text-body-md text-on-surface-variant">
        Controller id: {row.unifi_schedule_id ?? '— (pending)'}
      </p>
      <p className="font-body-md text-body-md text-on-surface-variant">
        Access Zones and amenities that consume this schedule can be linked
        from the{' '}
        <a className="underline" href="/access-groups">
          Access Zones
        </a>{' '}
        and{' '}
        <a className="underline" href="/amenities">
          Amenities
        </a>{' '}
        pages.
      </p>
    </div>
  );
}

interface DraftWindow {
  days: Weekday[];
  start: string;
  end: string;
}

const ALL_DAYS: Weekday[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const ALL_DAY_WINDOW: DraftWindow = { days: ALL_DAYS, start: '00:00', end: '23:59' };

function normalizeWindow(w: DraftWindow): ScheduleWindow {
  return { days: w.days, start: w.start, end: w.end };
}

function isAllDayWindow(w: { days: string[]; start: string; end: string }): boolean {
  return w.start === '00:00' && w.end === '23:59' && ALL_DAYS.every((day) => w.days.includes(day));
}

function formatWindow(w: { days: string[]; start: string; end: string }): string {
  if (isAllDayWindow(w)) return 'All day, every day';
  return `${w.days.join(', ')} ${w.start}-${w.end}`;
}

function CreateOrEditModal({
  schedule,
  buildingView,
  scopeOpts,
  onCancel,
  onDone,
}: {
  schedule: ScheduleRow | null;
  buildingView: boolean;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  // One idempotency key per modal lifetime (rotates on remount). Each
  // retry inside this same modal reuses the key so the backend collapses
  // duplicate controller side-effects to a single result.
  const { key: idemKey } = useIdempotencyKey();
  const [name, setName] = useState(schedule?.name ?? '');
  const [windows, setWindows] = useState<DraftWindow[]>(() =>
    schedule
      ? schedule.windows_json.map((w) => ({
          days: w.days,
          start: w.start,
          end: w.end,
        }))
      : [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '08:00', end: '17:00' }],
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<unknown>(null);
  const [holidayYear, setHolidayYear] = useState(() => new Date().getFullYear());
  const [selectedHolidayIds, setSelectedHolidayIds] = useState<string[]>([]);
  const [holidayOverrides, setHolidayOverrides] = useState<HolidayOverride[]>(
    () => schedule?.holiday_overrides_json ?? [],
  );
  const holidayPresets = useMemo(() => commonHolidayPresets(holidayYear), [holidayYear]);

  function addWindow() {
    setWindows((prev) => [
      ...prev,
      { days: ['mon'], start: '09:00', end: '17:00' },
    ]);
  }
  function updateWindow(idx: number, patch: Partial<DraftWindow>) {
    setWindows((prev) =>
      prev.map((w, i) => (i === idx ? { ...w, ...patch } : w)),
    );
  }
  function removeWindow(idx: number) {
    setWindows((prev) => prev.filter((_, i) => i !== idx));
  }
  function setAllDay() {
    setWindows([{ ...ALL_DAY_WINDOW, days: [...ALL_DAY_WINDOW.days] }]);
    if (!name.trim()) setName('24/7');
  }
  function toggleDay(idx: number, day: Weekday) {
    setWindows((prev) =>
      prev.map((w, i) => {
        if (i !== idx) return w;
        const has = w.days.includes(day);
        return {
          ...w,
          days: has ? w.days.filter((d) => d !== day) : [...w.days, day],
        };
      }),
    );
  }
  function toggleHoliday(id: string) {
    setSelectedHolidayIds((prev) =>
      prev.includes(id) ? prev.filter((current) => current !== id) : [...prev, id],
    );
  }
  function applySelectedHolidays() {
    const selectedDates = holidayPresets
      .filter((preset) => selectedHolidayIds.includes(preset.id))
      .map((preset) => preset.date);
    setHolidayOverrides((prev) => mergeClosedHolidayOverrides(prev, selectedDates));
    setSelectedHolidayIds([]);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setErrorDetails(null);

    // Light client-side validation before we burn a controller round-trip.
    if (!name.trim()) {
      setError('Name is required.');
      setSubmitting(false);
      return;
    }
    if (windows.length === 0) {
      setError('At least one window is required.');
      setSubmitting(false);
      return;
    }
    for (const w of windows) {
      if (w.days.length === 0) {
        setError('Each window must include at least one weekday.');
        setSubmitting(false);
        return;
      }
      if (!w.start || !w.end || w.start >= w.end) {
        setError('Window start must be strictly before end.');
        setSubmitting(false);
        return;
      }
    }

    const body = {
      name: name.trim(),
      windows: windows.map(normalizeWindow),
      holiday_overrides: holidayOverrides,
    };

    try {
      if (schedule) {
        await apiClient.withIdempotencyKey(idemKey).patch(
          buildingView ? `/api/v1/admin/schedules/${schedule.id}` : `/api/v1/schedules/${schedule.id}`,
          body,
          buildingView ? { skipTenantScope: true } : scopeOpts,
        );
        onDone('Schedule updated.');
      } else {
        await apiClient.withIdempotencyKey(idemKey).post(
          buildingView ? '/api/v1/admin/schedules' : '/api/v1/schedules',
          body,
          buildingView ? { skipTenantScope: true } : scopeOpts,
        );
        onDone('Schedule created.');
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(msg);
      if (err instanceof ApiError) setErrorDetails(err.details);
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={schedule ? `Edit ${schedule.name}` : 'New schedule'}
      icon={schedule ? 'edit_calendar' : 'add'}
      onCancel={onCancel}
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Name" htmlFor="sx-name">
          <input
            id="sx-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldCls}
          />
        </Field>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-label-md text-label-md text-on-surface uppercase">
              Windows
            </h3>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={setAllDay}
                className="inline-flex items-center gap-1 text-primary font-label-md text-label-md hover:underline"
              >
                <Icon name="schedule" className="text-base" />
                All day
              </button>
              <button
                type="button"
                onClick={addWindow}
                className="inline-flex items-center gap-1 text-primary font-label-md text-label-md hover:underline"
              >
                <Icon name="add" className="text-base" />
                Add window
              </button>
            </div>
          </div>
          {windows.map((w, idx) => (
            <div
              key={idx}
              className="rounded-lg border border-outline-variant p-3 space-y-2"
            >
              <div className="flex flex-wrap gap-1">
                {WEEKDAYS.map((d) => {
                  const active = w.days.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(idx, d)}
                      aria-pressed={active}
                      className={`px-2 py-1 rounded-full font-label-md text-label-md uppercase border ${
                        active
                          ? 'bg-primary text-on-primary border-primary'
                          : 'bg-surface text-on-surface border-outline-variant'
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <input
                  aria-label={`Window ${idx + 1} start`}
                  type="time"
                  value={w.start}
                  onChange={(e) =>
                    updateWindow(idx, { start: e.target.value })
                  }
                  className={`${fieldCls} w-32`}
                />
                <span className="text-on-surface-variant">–</span>
                <input
                  aria-label={`Window ${idx + 1} end`}
                  type="time"
                  value={w.end}
                  onChange={(e) => updateWindow(idx, { end: e.target.value })}
                  className={`${fieldCls} w-32`}
                />
                <div className="flex-1" />
                {windows.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeWindow(idx)}
                    className="text-error hover:underline font-label-md text-label-md"
                    aria-label={`Remove window ${idx + 1}`}
                  >
                    Remove
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-label-md text-label-md text-on-surface uppercase">
              Holiday closures
            </h3>
            <label className="flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant">
              <span>Year</span>
              <input
                aria-label="Holiday preset year"
                type="number"
                min="2020"
                max="2100"
                value={holidayYear}
                onChange={(e) => setHolidayYear(Number(e.target.value))}
                className={`${fieldCls} w-28 py-1`}
              />
            </label>
          </div>
          <div className="rounded-lg border border-outline-variant divide-y divide-outline-variant">
            {holidayPresets.map((preset) => (
              <label key={preset.id} className="flex items-center gap-3 px-3 py-2">
                <input
                  type="checkbox"
                  checked={selectedHolidayIds.includes(preset.id)}
                  onChange={() => toggleHoliday(preset.id)}
                  aria-label={preset.label}
                />
                <span className="flex-1 font-body-md text-body-md text-on-surface">
                  {preset.label}
                </span>
                <span className="font-body-sm text-body-sm text-on-surface-variant">
                  {preset.date}
                </span>
              </label>
            ))}
          </div>
          <div className="flex justify-between gap-3">
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Selected holidays are added as closed overrides and synced with the schedule.
            </p>
            <button
              type="button"
              onClick={applySelectedHolidays}
              disabled={selectedHolidayIds.length === 0}
              className="whitespace-nowrap text-primary font-label-md text-label-md hover:underline disabled:text-on-surface-variant disabled:no-underline"
            >
              Apply selected holidays
            </button>
          </div>
          {holidayOverrides.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {holidayOverrides.map((override) => (
                <span
                  key={override.date}
                  className="inline-flex items-center gap-2 rounded-full bg-surface-container px-3 py-1 font-body-sm text-body-sm text-on-surface"
                >
                  {override.date} {override.mode}
                  <button
                    type="button"
                    className="text-error hover:underline"
                    onClick={() =>
                      setHolidayOverrides((prev) => removeHolidayOverride(prev, override.date))
                    }
                    aria-label={`Remove holiday override ${override.date}`}
                  >
                    Remove
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </div>

        {error ? (
          <div role="alert" className="space-y-1">
            <p className="text-error font-body-md text-body-md">{error}</p>
            {errorDetails &&
            typeof errorDetails === 'object' &&
            'issues' in errorDetails ? (
              <pre className="font-code text-code text-error overflow-x-auto bg-error-container/40 rounded p-2">
                {JSON.stringify(
                  (errorDetails as { issues: unknown }).issues,
                  null,
                  2,
                )}
              </pre>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {schedule ? 'Save changes' : 'Create schedule'}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteModal({
  schedule,
  buildingView,
  scopeOpts,
  onCancel,
  onDone,
}: {
  schedule: ScheduleRow;
  buildingView: boolean;
  scopeOpts?: ApiRequestInit;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { key: idemKey } = useIdempotencyKey();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  async function confirm() {
    setSubmitting(true);
    setError(null);
    try {
      await apiClient
        .withIdempotencyKey(idemKey)
        .delete(
          buildingView ? `/api/v1/admin/schedules/${schedule.id}` : `/api/v1/schedules/${schedule.id}`,
          buildingView ? { skipTenantScope: true } : scopeOpts,
        );
      onDone('Schedule deleted.');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : (err as Error).message;
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <ModalShell
      title={`Delete ${schedule.name}`}
      icon="delete"
      destructive
      onCancel={onCancel}
    >
      <div className="space-y-4">
        <p className="font-body-md text-body-md text-on-surface">
          This removes the schedule from the portal and the UniFi controller.
          Access Zones that reference this schedule will fall back to no
          access until you reassign them.
        </p>
        <p className="font-body-md text-body-md text-on-surface">
          Type <span className="font-mono font-semibold">{schedule.name}</span>{' '}
          to confirm.
        </p>
        <input
          aria-label="Confirm schedule name"
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className={fieldCls}
        />
        {error ? (
          <p role="alert" className="text-error font-body-md text-body-md">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            loading={submitting}
            disabled={confirmText !== schedule.name}
            onClick={confirm}
          >
            Delete
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

const fieldCls =
  'w-full px-3 py-2 bg-surface border border-outline-variant rounded-lg font-body-md text-body-md focus:ring-2 focus:ring-primary outline-none';

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <label
        htmlFor={htmlFor}
        className="font-label-md text-label-md text-on-surface block"
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function ModalShell({
  title,
  icon,
  destructive,
  onCancel: _onCancel,
  children,
}: {
  title: string;
  icon: string;
  destructive?: boolean;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby="schedule-action-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/30 p-margin-mobile"
    >
      <div className="w-full max-w-[640px] bg-surface-container-lowest rounded-xl shadow-lg border border-outline-variant p-8 max-h-[90vh] overflow-y-auto">
        <header className="flex items-center gap-3 mb-6">
          <div
            className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${
              destructive
                ? 'bg-error-container text-on-error-container'
                : 'bg-primary-container text-on-primary'
            }`}
          >
            <Icon name={icon} />
          </div>
          <h2
            id="schedule-action-title"
            className="font-headline-sm text-headline-sm text-on-surface"
          >
            {title}
          </h2>
        </header>
        {children}
      </div>
    </div>
  );
}
